import { create } from "zustand";

// ===== Global Radio Player Store =====
// Uses Zustand so state survives page navigation
// Audio element is created once and persists

let audioElement = null;

function getAudio() {
  if (!audioElement) {
    audioElement = new Audio();
    audioElement.preload = "auto";
  }
  return audioElement;
}

// ===== Live Broadcast Time-Sync =====
// Uses wall-clock time so every listener joins the program at the same point.
// totalDuration is the sum of all segment durations. We modulo the current
// Unix timestamp by totalDuration to get a deterministic "broadcast offset"
// that all clients independently calculate to the same value.
const DEFAULT_TRACK_SECS = 180; // 3-min fallback for tracks with missing duration

function getLiveSyncPos(segments) {
  if (!segments || segments.length === 0) return { index: 0, startTime: 0 };

  const totalDuration = segments.reduce(
    (sum, s) => sum + (s.durationSeconds > 0 ? s.durationSeconds : DEFAULT_TRACK_SECS),
    0
  );
  if (totalDuration === 0) return { index: 0, startTime: 0 };

  // Offset from the Unix epoch — identical for all clients at this moment
  const offset = Math.floor(Date.now() / 1000) % totalDuration;

  let cumulative = 0;
  for (let i = 0; i < segments.length; i++) {
    const dur = segments[i].durationSeconds > 0 ? segments[i].durationSeconds : DEFAULT_TRACK_SECS;
    if (offset < cumulative + dur) {
      return { index: i, startTime: Math.max(0, offset - cumulative) };
    }
    cumulative += dur;
  }
  return { index: 0, startTime: 0 };
}

export const useRadioStore = create((set, get) => ({
  // Playback state
  playing: false,
  loading: false,
  currentStation: null,
  currentTrack: null,
  currentPlan: null,
  currentSegmentIndex: 0,
  volume: 0.8,
  muted: false,
  visible: false,

  // Shuffle
  shuffle: false,
  shuffleOrder: [],    // pre-computed shuffled index list
  shufflePosition: 0, // where we are in shuffleOrder

  // Now playing from Firestore
  nowPlayingData: null,

  // Initialize audio listeners
  _initialized: false,
  init() {
    if (get()._initialized) return;
    const audio = getAudio();
    audio.volume = get().volume;

    audio.addEventListener("ended", () => {
      // Auto-advance respecting shuffle order
      get().playNext();
    });

    audio.addEventListener("playing", () => set({ playing: true, loading: false }));
    audio.addEventListener("waiting", () => set({ loading: true }));
    audio.addEventListener("canplay", () => set({ loading: false }));
    audio.addEventListener("error", (e) => {
      console.warn("Radio audio error:", e);
      set({ loading: false, playing: false });
    });

    set({ _initialized: true });
  },

  async playStation(station, plan) {
    get().init();
    const audio = getAudio();

    set({ currentStation: station, currentPlan: plan || null, visible: true, loading: true });

    if (plan?.segments?.length > 0) {
      // Jump into the broadcast at the same position every listener would be at right now
      const { index, startTime } = getLiveSyncPos(plan.segments);
      get().playSegment(index, startTime);
    } else if (station?.streamURL) {
      audio.src = station.streamURL;
      audio.load();
      try {
        await audio.play();
        set({ playing: true, loading: false });
      } catch (e) {
        console.warn("Playback blocked:", e);
        set({ playing: false, loading: false });
      }
    } else {
      set({ loading: false });
    }
  },

  // startTime: seconds into the segment to begin from (used for live sync on tune-in)
  async playSegment(index, startTime = 0) {
    get().init();
    const { currentPlan } = get();
    const segment = currentPlan?.segments?.[index];
    if (!segment?.mp3FileUrl) return;

    const audio = getAudio();
    audio.src = segment.mp3FileUrl;

    // Apply seek via loadedmetadata so the browser has range data before we seek
    if (startTime > 0) {
      const applySeek = () => {
        audio.currentTime = startTime;
        audio.removeEventListener("loadedmetadata", applySeek);
      };
      audio.addEventListener("loadedmetadata", applySeek);
    }

    audio.load();
    try {
      await audio.play();
      set({
        playing: true,
        loading: false,
        currentSegmentIndex: index,
        currentTrack: {
          title: segment.title || "Unknown Track",
          artist: segment.artist || "",
          artworkUrl: segment.artworkUrl || null,
          durationSeconds: segment.durationSeconds || 0,
          showTitle: segment.showTitle || null,
          segmentType: segment.segmentType || "track",
        },
      });
    } catch (e) {
      console.warn("Segment playback error:", e);
      set({ loading: false });
    }
  },

  async playTrack(track) {
    get().init();
    if (!track?.mp3FileUrl && !track?.storageURL) return;
    const url = track.mp3FileUrl || track.storageURL;
    const audio = getAudio();
    audio.src = url;
    audio.load();
    try {
      await audio.play();
      set({
        playing: true,
        loading: false,
        visible: true,
        currentTrack: {
          title: track.title || "Unknown Track",
          artist: track.artist || "",
          artworkUrl: track.artworkUrl || null,
          durationSeconds: track.durationSeconds || 0,
        },
      });
    } catch (e) {
      console.warn("Track playback error:", e);
    }
  },

  async togglePlay() {
    get().init();
    const audio = getAudio();
    const { playing } = get();
    if (playing) {
      audio.pause();
      set({ playing: false });
    } else {
      try {
        await audio.play();
        set({ playing: true });
      } catch (e) {
        console.warn("Resume failed:", e);
      }
    }
  },

  setVolume(v) {
    const audio = getAudio();
    audio.volume = v;
    set({ volume: v, muted: v === 0 });
  },

  toggleMute() {
    const { muted, volume } = get();
    const audio = getAudio();
    if (muted) {
      audio.volume = volume || 0.8;
      set({ muted: false });
    } else {
      audio.volume = 0;
      set({ muted: true });
    }
  },

  stop() {
    const audio = getAudio();
    audio.pause();
    audio.src = "";
    set({ playing: false, visible: false, currentStation: null, currentTrack: null, currentPlan: null });
  },

  setNowPlaying(data) {
    set({ nowPlayingData: data });
    if (data?.currentTrackTitle) {
      set((s) => ({
        currentTrack: {
          ...s.currentTrack,
          title: data.currentTrackTitle,
          artist: data.currentTrackArtist || "",
          artworkUrl: data.currentArtworkUrl || s.currentTrack?.artworkUrl,
        },
      }));
    }
  },

  // Play any content (video or audio) through the global audio element.
  // startTime lets us resume from where a video was playing on navigation.
  playContent(content, startTime = 0) {
    const url =
      content.videoURL || content.videoUrl ||
      content.storageURL || content.url ||
      content.audioURL || content.mp3FileUrl || "";
    if (!url) return;
    get().init();
    const audio = getAudio();
    audio.src = url;
    audio.load();
    const onCanPlay = () => {
      if (startTime > 0) audio.currentTime = startTime;
      audio.play().catch(() => {});
      audio.removeEventListener("canplay", onCanPlay);
    };
    audio.addEventListener("canplay", onCanPlay);
    set({
      visible: true,
      loading: true,
      playing: false,
      currentStation: null,
      currentPlan: null,
      currentSegmentIndex: 0,
      shuffleOrder: [],
      shufflePosition: 0,
      currentTrack: {
        title: content.title || "Now Playing",
        artist: content.creatorName || "",
        artworkUrl: content.thumbnailURL || content.thumbnailUrl || null,
        contentType: content.type || "video",
        contentId: content.id || "",
      },
    });
  },

  toggleShuffle() {
    const { shuffle, currentPlan, currentSegmentIndex } = get();
    if (!shuffle && currentPlan?.segments?.length > 0) {
      // Build Fisher-Yates shuffled index order
      const len = currentPlan.segments.length;
      const order = Array.from({ length: len }, (_, i) => i);
      for (let i = len - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [order[i], order[j]] = [order[j], order[i]];
      }
      // Keep current track as position 0 in shuffle order for continuity
      const curPos = order.indexOf(currentSegmentIndex);
      if (curPos > 0) {
        [order[0], order[curPos]] = [order[curPos], order[0]];
      }
      set({ shuffle: true, shuffleOrder: order, shufflePosition: 0 });
    } else {
      set({ shuffle: false, shuffleOrder: [], shufflePosition: 0 });
    }
  },

  playNext() {
    const { currentPlan, currentSegmentIndex, shuffle, shuffleOrder, shufflePosition } = get();
    const segments = currentPlan?.segments || [];
    if (segments.length === 0) {
      // No plan = content playback, stop when done
      set({ playing: false, currentTrack: null });
      return;
    }
    if (shuffle && shuffleOrder.length > 0) {
      // Shuffle always wraps around
      const nextPos = (shufflePosition + 1) % shuffleOrder.length;
      set({ shufflePosition: nextPos });
      get().playSegment(shuffleOrder[nextPos]);
    } else {
      // Live radio loops back to the start — never stops
      const nextIdx = (currentSegmentIndex + 1) % segments.length;
      get().playSegment(nextIdx);
    }
  },

  playPrev() {
    const { currentPlan, currentSegmentIndex, shuffle, shuffleOrder, shufflePosition } = get();
    const segments = currentPlan?.segments || [];
    if (segments.length === 0) return;
    if (shuffle && shuffleOrder.length > 0) {
      const prevPos = (shufflePosition - 1 + shuffleOrder.length) % shuffleOrder.length;
      set({ shufflePosition: prevPos });
      get().playSegment(shuffleOrder[prevPos]);
    } else {
      // Wrap to last track when at the beginning
      const prevIdx = (currentSegmentIndex - 1 + segments.length) % segments.length;
      get().playSegment(prevIdx);
    }
  },
}));
